/**
 * useUserPreference — a tiny per-user UI flag that follows the user across
 * machines (server-backed via /api/me/preferences/:key, NOT localStorage).
 * Mirrors the toolbar-pins pattern: read the server value (fallback until it
 * loads / when unset), and write through optimistically on every change.
 */
import { useCallback, useContext, useRef } from "react";
import { QueryClient, QueryClientContext, useQuery } from "@tanstack/react-query";
import { api } from "./api";

/**
 * Used only when nothing above provides a QueryClient — a component test
 * that mounts a bare DataTable. Every DataTable reads a saved sort through
 * this hook (disabled when the table has no `sortId`), and the grid must not
 * start requiring a provider it never needed before.
 */
let detachedClient: QueryClient | null = null;

export function useUserPreference<T>(
    key: string,
    fallback: T,
    options?: {
        /**
         * Called when the write-through fails, AFTER the optimistic value has
         * been rolled back. Optional so every existing caller is unchanged —
         * but a preference the user set by hand (dragging their mailboxes into
         * order) must not look saved when it wasn't, so those callers pass this
         * and say so on screen.
         */
        onError?: (error: unknown) => void;
        /**
         * False skips the server entirely: the fallback is the value and `set`
         * only updates the local cache. For hooks that persist only when the
         * caller gave them a key (a saved table sort).
         */
        enabled?: boolean;
    },
): [T, (value: T) => void, { loaded: boolean }] {
    const enabled = options?.enabled ?? true;
    const qc = useContext(QueryClientContext) ?? (detachedClient ??= new QueryClient());
    // Kept in a ref so a caller can pass an inline arrow without re-creating
    // `set` (and thus every effect that depends on it) on every render.
    const onError = useRef(options?.onError);
    onError.current = options?.onError;
    const queryKey = ["user-preference", key];
    const q = useQuery(
        {
            queryKey,
            queryFn: () => api.get<{ value: T | null }>(`/api/me/preferences/${encodeURIComponent(key)}`),
            staleTime: 5 * 60 * 1000,
            enabled,
        },
        qc,
    );
    const value = q.data && q.data.value != null ? q.data.value : fallback;
    const set = useCallback(
        (next: T) => {
            qc.setQueryData(queryKey, { value: next });
            // A read still in flight would land after this and put the old
            // value back — a sort clicked while the page was loading would
            // snap back a moment later. The click is newer: drop the read,
            // and set again once the cancel has rolled the query back.
            void qc.cancelQueries({ queryKey }).then(() => qc.setQueryData(queryKey, { value: next }));
            if (!enabled) return;
            api.put(`/api/me/preferences/${encodeURIComponent(key)}`, { value: next }).catch((error) => {
                // Best-effort write-through; refetch to reconcile on failure —
                // then tell the caller, so a hand-set preference can report that
                // it did NOT save instead of showing a change that's already gone.
                qc.invalidateQueries({ queryKey });
                onError.current?.(error);
            });
        },
        // queryKey is derived from the stable `key` prop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [qc, key, enabled],
    );
    // An error counts as loaded: the fallback is the answer, and a caller
    // waiting on `loaded` must not wait forever on a network blip.
    return [value, set, { loaded: !enabled || q.isFetched || q.isError }];
}
